import { Link } from "react-router-dom";
import {
  useGetMyChapterApplicationsQuery,
  useGetMyChapterUpgradeApplicationsQuery,
} from "../api/chapterApi";
import { UserCheck, ArrowUpCircle, ChevronRight, Clock } from "lucide-react";

// Applications awaiting the chapter's action.
function pendingProb(rows: any[] | undefined) {
  if (!Array.isArray(rows)) return 0;
  // `awaiting_endorsements` is the same rung as `acknowledged` under an older
  // name — an application sitting on it is still waiting for this chapter.
  return rows.filter(
    (a) =>
      ["acknowledged", "awaiting_endorsements", "returned"].includes(a?.status) &&
      !a?.chapter_accepted_at,
  ).length;
}
function pendingUpgrade(rows: any[] | undefined) {
  if (!Array.isArray(rows)) return 0;
  return rows.filter(
    (a) => ["submitted", "under_review"].includes(a?.status) && !a?.chapter_accepted_at,
  ).length;
}

function Card({
  to,
  title,
  subtitle,
  icon,
  pending,
  accent,
}: {
  to: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  pending: number;
  accent: string;
}) {
  return (
    <Link
      to={to}
      className="group flex flex-col justify-between bg-white border border-gray-200 rounded-[32px] p-8 shadow-sm hover:shadow-md hover:border-gray-300 transition-all"
    >
      <div className="flex items-start justify-between">
        <div className={`w-14 h-14 rounded-2xl grid place-items-center ${accent}`}>{icon}</div>
        {pending > 0 && (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100 text-xs font-black">
            <Clock className="w-3.5 h-3.5" /> {pending} awaiting
          </span>
        )}
      </div>
      <div className="mt-6">
        <h3 className="text-xl font-black text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500 font-medium mt-1">{subtitle}</p>
      </div>
      <div className="mt-6 flex items-center gap-1 text-sm font-bold text-gray-900">
        Review applications
        <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </Link>
  );
}

export default function ChapterReferralsPage() {
  const { data: prob } = useGetMyChapterApplicationsQuery();
  const { data: upgrade } = useGetMyChapterUpgradeApplicationsQuery();

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8">
      <div className="space-y-1">
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">Chapter Referrals</h1>
        <p className="text-sm text-gray-500 font-medium">
          Review, endorse and reject applications submitted from your chapter.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card
          to="/chapter/my-applications"
          title="Application Referrals"
          subtitle="Membership (probationer) applications from your chapter."
          icon={<UserCheck className="w-7 h-7 text-blue-600" />}
          accent="bg-blue-50"
          pending={pendingProb(prob?.data)}
        />
        <Card
          to="/chapter/my-upgrade-applications"
          title="Upgrade Referrals"
          subtitle="Membership grade-upgrade applications from your chapter."
          icon={<ArrowUpCircle className="w-7 h-7 text-indigo-600" />}
          accent="bg-indigo-50"
          pending={pendingUpgrade(upgrade?.data)}
        />
      </div>
    </div>
  );
}
